import React from 'react';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
} from '@mui/material';

const Support = () => {
  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      <Container maxWidth="lg">
        <Box sx={{ pt: 6, pb: 4 }}>
          {/* Header Section */}
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Typography
              component="h1"
              variant="h2"
              gutterBottom
              sx={{
                fontWeight: 700,
                background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 2,
              }}
            >
              Support Center
            </Typography>
            <Typography
              variant="h5"
              color="text.secondary"
              sx={{ 
                mb: 4, 
                maxWidth: '700px', 
                mx: 'auto',
                fontWeight: 400,
                lineHeight: 1.5,
              }}
            >
              Get help from our community or reach out to our support team directly
            </Typography>
          </Box>

          {/* Main Content Grid */}
          <Grid container spacing={4}>
            {/* Left side - Community Chat (2/3) */}
            <Grid item xs={12} md={8}>
              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  height: '700px',
                  bgcolor: 'background.paper',
                  borderRadius: '20px',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  background: 'linear-gradient(145deg, rgba(30, 41, 59, 0.8) 0%, rgba(15, 23, 42, 0.9) 100%)',
                  backdropFilter: 'blur(20px)',
                  transition: 'all 0.3s ease-in-out',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
                    borderColor: 'rgba(99, 102, 241, 0.3)',
                  },
                }}
              >
                <Typography 
                  variant="h5" 
                  gutterBottom
                  sx={{ 
                    fontWeight: 600,
                    mb: 3,
                    color: 'text.primary'
                  }}
                >
                  Community Support Chat
                </Typography>
                <Box
                  sx={{
                    position: 'relative',
                    width: '100%',
                    height: 'calc(100% - 80px)',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    border: '2px solid rgba(99, 102, 241, 0.2)',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      borderColor: 'rgba(99, 102, 241, 0.5)',
                      boxShadow: '0 0 30px rgba(99, 102, 241, 0.2)',
                    },
                  }}
                >
                  <iframe
                    src="https://wizcord.io/iframe?channelId=1396513175699984544&menu=false"
                    width="100%"
                    height="100%"
                    style={{ border: 'none' }}
                    title="Support Discord Chat"
                    allow="clipboard-write"
                  />
                </Box>
              </Paper>
            </Grid>

            {/* Right side - Contact Information (1/3) */}
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, height: '700px' }}>
                {/* Direct Support Card */}
                <Card
                  sx={{
                    flex: 1,
                    background: 'linear-gradient(145deg, rgba(30, 41, 59, 0.6) 0%, rgba(15, 23, 42, 0.8) 100%)',
                    backdropFilter: 'blur(10px)',
                    borderRadius: '16px',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
                      borderColor: 'rgba(99, 102, 241, 0.3)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 4, textAlign: 'center' }}>
                    <Box
                      sx={{
                        width: 60,
                        height: 60,
                        borderRadius: '50%',
                        background: 'linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        mx: 'auto',
                        mb: 3,
                        boxShadow: '0 10px 30px rgba(99, 102, 241, 0.3)',
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: '1.5rem',
                          fontWeight: 700,
                          color: 'white',
                        }}
                      >
                        @
                      </Typography>
                    </Box>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ fontWeight: 600, mb: 2 }}
                    >
                      Email Support
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ mb: 3, lineHeight: 1.6 }}
                    >
                      Need direct assistance? Send us an email and we'll get back to you within 24 hours.
                    </Typography>
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: 'rgba(99, 102, 241, 0.1)',
                        borderRadius: '8px',
                        border: '1px solid rgba(99, 102, 241, 0.2)',
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: 'primary.main',
                          fontFamily: 'monospace',
                          fontSize: '0.95rem',
                          wordBreak: 'break-all',
                        }}
                      >
                        support@wizcord.io
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>

                {/* Response Time Card */}
                <Card
                  sx={{
                    background: 'linear-gradient(145deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.15) 100%)',
                    borderRadius: '16px',
                    border: '1px solid rgba(16, 185, 129, 0.2)',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      borderColor: 'rgba(16, 185, 129, 0.4)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 3, textAlign: 'center' }}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ fontWeight: 600, color: 'secondary.main' }}
                    >
                      Quick Response
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ lineHeight: 1.5 }}
                    >
                      We typically respond to support emails within 24 hours during business days.
                    </Typography>
                  </CardContent>
                </Card>

                {/* Community Info Card */}
                <Card
                  sx={{
                    background: 'linear-gradient(145deg, rgba(139, 92, 246, 0.1) 0%, rgba(124, 58, 237, 0.15) 100%)',
                    borderRadius: '16px',
                    border: '1px solid rgba(139, 92, 246, 0.2)',
                    transition: 'all 0.3s ease-in-out',
                    '&:hover': {
                      borderColor: 'rgba(139, 92, 246, 0.4)',
                    },
                  }}
                >
                  <CardContent sx={{ p: 3, textAlign: 'center' }}>
                    <Typography
                      variant="h6"
                      gutterBottom
                      sx={{ fontWeight: 600, color: 'primary.light' }}
                    >
                      Community Help
                    </Typography>
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ lineHeight: 1.5 }}
                    >
                      Join our Discord community for real-time help from other users and our team.
                    </Typography>
                  </CardContent>
                </Card>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </Box>
  );
};

export default Support; 